/**
 * useElementContentWidth — the CONTENT-box width of one element, kept current
 * by a ResizeObserver (header rail tiers, 2026-09-15).
 *
 * Content box on purpose: it is the box `@container … (inline-size)` queries
 * evaluate against, so a JS decision made from this number and a CSS rule
 * made from a container query agree about the same pane. `useDraftTall`
 * (composerControlsLayout.ts) is the sibling idiom — measure the box, never
 * infer it.
 *
 * The FIRST sample is synchronous, in a layout effect, before the browser
 * paints: without it every tablet mount (and every thread switch that
 * remounts the header) would flash the eight-button rail for a frame and then
 * fold. Degrades to `null` wherever it cannot measure — no element, a server
 * render, no ResizeObserver — and the caller's rule treats null as "today's
 * behaviour".
 */
import { useLayoutEffect, useRef, useState } from 'react';

function contentWidthOf(el: HTMLElement): number | null {
  if (typeof window === 'undefined') return null;
  const rect = el.getBoundingClientRect();
  if (!rect || !Number.isFinite(rect.width) || rect.width <= 0) return null;
  const cs = window.getComputedStyle(el);
  const side = (v: string): number => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const inner = rect.width
    - side(cs.paddingLeft) - side(cs.paddingRight)
    - side(cs.borderLeftWidth) - side(cs.borderRightWidth);
  return inner > 0 ? Math.round(inner) : null;
}

export function useElementContentWidth(
  ref: { current: HTMLElement | null },
  /**
   * Stop publishing while true (rail drag, 2026-09-20). The full-screen rail's
   * width IS this element's `padding-left`, so dragging the divider changes
   * the observed content box on every frame — and every change is a `setWidth`
   * that re-renders the whole chat host. That is the cost headerRailTiers.ts
   * was written to avoid ("a raw pane width would re-render the whole
   * transcript on every rounded pixel"), so the gesture suspends this and the
   * effect below publishes once when it lets go. Default false ⇒ byte-for-byte
   * the behaviour every other caller has always had.
   */
  suspended: boolean = false,
): number | null {
  const [width, setWidth] = useState<number | null>(null);
  const widthRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (suspended) return undefined;
    const el = ref.current;
    if (!el) {
      if (widthRef.current !== null) {
        widthRef.current = null;
        setWidth(null);
      }
      return undefined;
    }
    let frame: number | null = null;
    const publish = () => {
      frame = null;
      const next = contentWidthOf(el);
      if (next !== widthRef.current) {
        widthRef.current = next;
        setWidth(next);
      }
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      // ResizeObserver reports the content box directly; prefer it, but the
      // reconcile below re-measures so a browser that reports a stale entry
      // during a transition still converges on the real box.
      const entry = entries[0];
      const reported = entry?.contentRect?.width;
      if (typeof reported === 'number' && Number.isFinite(reported) && reported > 0) {
        const rounded = Math.round(reported);
        if (rounded !== widthRef.current) {
          widthRef.current = rounded;
          setWidth(rounded);
        }
      }
      if (frame === null && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        frame = window.requestAnimationFrame(publish);
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (frame !== null && typeof window !== 'undefined') window.cancelAnimationFrame(frame);
    };
  }, [ref, suspended]);

  return width;
}
